import React, { useEffect, useState, useRef, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import api from '@/lib/axiosInstance'
import type { PropertyListings } from '@/types/PropertyListings'
import Helper from '@/lib/helper'
import { Spinner } from '@/components/ui/shadcn-io/spinner'
import { Circle, GoogleMap, Marker, OverlayView } from '@react-google-maps/api'
import { Home, ListFilter, Loader2, MapPin, Search, X, Map as MapIcon, List, Send, Bed, Car, Bath, Eye } from 'lucide-react'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import { Input } from '@/components/ui/input'
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from "@/components/ui/drawer"
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
    Dialog,
    DialogContent,
} from "@/components/ui/dialog"
import { Label } from '@/components/ui/label'
import { Slider } from '@/components/ui/slider'
import {
    Avatar,
    AvatarFallback,
    AvatarImage,
} from "@/components/ui/avatar"
import { Badge } from '@/components/ui/badge'
import { useGoogleMaps } from '@/components/GoogleMapsProvider'
import type { RootState } from '@/lib/redux/store'
import { useSelector } from 'react-redux'
import Details from '../listings/view/details'
  
const defaultCenter = {
    lat: 3.139,
    lng: 101.6869
};

const CoListings = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const [offset, setOffset] = useState(0);
    const [limit] = useState(300);

    const [listings, setListings] = useState<PropertyListings[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const [selectedListing, setSelectedListing] = useState<PropertyListings | null>(null);
    const [mapCenter, setMapCenter] = useState(defaultCenter);
    const [mapZoom, setMapZoom] = useState(12);
    const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
    const [initialBounds, setInitialBounds] = useState<google.maps.LatLngBounds | null>(null);
    
    const [searchQuery, setSearchQuery] = useState('');
    const [criterias, setCriterias] = useState<any>({
        forSaleOrRentOptions: [],
        listingTypes: [],
        projectTypes: [],
        propertyTypes: [],
        propertySubTypes: [],
        states: [],
    })
  
    const [selectedType, setSelectedType] = useState<number | null>(null)
    const [selectedCategory, setSelectedCategory] = useState<number | null>(null)
    const [selectedState, setSelectedState] = useState<number | null>(null)
    const [selectedProjectType, setSelectedProjectType] = useState<string | null>(null)
    const [selectedPropertyType, setSelectedPropertyType] = useState<number | null>(null)
    const [selectedSubType, setSelectedSubType] = useState<string | null>(null)
    const [selectedPrice, setSelectedPrice] = useState<[number, number] | null>(null);
    const [applying, setApplying] = useState<boolean>(false)
    
    const [appliedFilters, setAppliedFilters] = useState<any>({})
    
    const [activeTab, setActiveTab] = useState<'map' | 'list'>('list')
    
    const [mapType, setMapType] = useState<'roadmap' | 'satellite'>('satellite')
    
    const divRef = useRef<HTMLDivElement>(null)
    const listViewRef = useRef<HTMLDivElement>(null)
    const markersRef = useRef<google.maps.Marker[]>([]);
    const clustererRef = useRef<MarkerClusterer | null>(null);
    const overlayViewsRef = useRef<Set<number>>(new Set());
    const isInitialLoadRef = useRef(true);
    const mapLoadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const [open, setOpen] = useState<boolean>(false);
    
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [pendingListingId, setPendingListingId] = useState<number | null>(null);
    
    const [previewListing, setPreviewListing] = useState<PropertyListings | null>(null);
    const [showPreview, setShowPreview] = useState(false);
    
    const { isLoaded } = useGoogleMaps();
    
    const filteredSubTypes =
        selectedPropertyType && criterias.propertySubTypes
        ? criterias.propertySubTypes.filter((s: any) => s.propertyTypeId === selectedPropertyType)
        : [];
        
    const handleReset = () => {
        setSelectedType(null);
        setSelectedCategory(null);
        setSelectedState(null);
        setSelectedProjectType(null);
        setSelectedPropertyType(null);
        setSelectedSubType(null);
        setSelectedPrice(null);
        setSearchQuery('');
        setAppliedFilters({});
        setOpen(false);
    };

    const getListingCriterias = async () => {
        try {
            const response = await api.get(`/listings/criterias`)
            if (!response.data.status) {
                throw new Error(response.data.error)
            }
            setCriterias(response.data)
        } catch (err: any) {
            toast(Helper.errorHandler(err))
        }
    }

    const getListings = async (loadMore = false) => {
        try {
            console.log('Getting listings', loadMore ? 'with load more' : 'initial');
            setLoading(true);
            const currentOffset = loadMore ? offset + limit : 0;
            let filters = {
                ...appliedFilters,
                offset: currentOffset,
                limit: limit
            };
            const response = await api.post(`/co-listings`, filters);
            if (!response.data.status) {
                throw new Error(response.data.error);
            };
            
            const newListings = response.data.listings || [];
            const mapPins = response.data.mapPins || [];
            const center = response.data.center;
            const bounds = response.data.bounds;
            
            if (loadMore) {
                setListings(prev => [...prev, ...newListings]);
                setOffset(currentOffset);
            } else {
                setListings(newListings);
                setOffset(0);
                
                if (mapInstance) {
                    if (center && !appliedFilters.north) {
                        setMapCenter({
                            lat: center.lat,
                            lng: center.lng
                        })
                        mapInstance.setZoom(11);
                    } else if (bounds && mapPins.length > 0) {
                        const googleBounds = new google.maps.LatLngBounds(
                            new google.maps.LatLng(bounds.south, bounds.west),
                            new google.maps.LatLng(bounds.north, bounds.east)
                        );
                        
                        if (!googleBounds.isEmpty()) {
                            setInitialBounds(googleBounds);
                            const padding = { top: 50, right: 50, bottom: 50, left: 50 };
                            mapInstance.fitBounds(googleBounds, padding);
                            console.log('📍 Fitted map to results bounds');
                        }
                    } else if (newListings.length > 0) {
                        const listingBounds = new google.maps.LatLngBounds();
                        newListings.forEach((listing: PropertyListings) => {
                            if (listing.lat && listing.lng) {
                                listingBounds.extend(new google.maps.LatLng(Number(listing.lat), Number(listing.lng)));
                            }
                        });
                        if (!listingBounds.isEmpty()) {
                            setInitialBounds(listingBounds);
                            const padding = { top: 50, right: 50, bottom: 50, left: 50 };
                            mapInstance.fitBounds(listingBounds, padding);
                            console.log('📍 Fitted map to listing bounds (fallback)');
                        }
                    }
                }
    
                if (mapPins.length > 0) {
                    console.log(`📍 Loaded ${mapPins.length} map pins`);
                }
            }
            
            setTotal(response.data.total);
            setHasMore(response.data.hasMore);
            if (response.data.mapPinsLimited) {
                console.warn(`⚠️ Showing ${mapPins.length} of ${response.data.totalInArea} pins in area`);
            }
        } catch (err: any) {
            toast.error(err.response?.data.error ?? err.message ?? 'Something went wrong');
        } finally {
            setLoading(false);
        }
    };

    const clearMarkers = () => {
        if (clustererRef.current) {
            clustererRef.current.clearMarkers();
            clustererRef.current = null;
        }
        
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        overlayViewsRef.current.clear();
    };

    const createMarkers = (map: google.maps.Map, listingsToRender: PropertyListings[]) => {
        clearMarkers();

        const markers = listingsToRender
            .filter(listing => listing.lat && listing.lng)
            .map(listing => {
                const marker = new google.maps.Marker({
                    position: { lat: Number(listing.lat), lng: Number(listing.lng) },
                    map: map,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                        fillColor: '#2563eb',
                        fillOpacity: 1,
                        strokeWeight: 0
                    },
                    title: listing.headline
                });

                marker.addListener('click', () => {
                    handleMarkerClick(listing);
                });

                return marker;
            });

        markersRef.current = markers;

        clustererRef.current = new MarkerClusterer({
            map,
            markers,
            renderer: {
                render: ({ count, position }) => {
                    const color = count > 100 ? '#dc2626' : count > 50 ? '#ea580c' : count > 10 ? '#2563eb' : '#0891b2';
                    const size = count > 100 ? 50 : count > 50 ? 45 : count > 10 ? 40 : 35;
                    
                    return new google.maps.Marker({
                        position,
                        icon: {
                            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
                                <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
                                    <circle cx="${size/2}" cy="${size/2}" r="${size/2 - 2}" fill="${color}" stroke="white" stroke-width="3"/>
                                    <text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="white" font-family="Arial, sans-serif" font-size="${size > 40 ? 14 : 12}" font-weight="bold">${count}</text>
                                </svg>
                            `)}`,
                            scaledSize: new google.maps.Size(size, size),
                        },
                        label: '',
                        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                    });
                }
            }
        });
    };

    const handleMapLoad = (map: google.maps.Map) => {
        console.log('Map loaded - setting initial load state');
        setMapInstance(map);
        isInitialLoadRef.current = true;
        
        if (listings.length > 0) {
            const bounds = new google.maps.LatLngBounds();
            listings.forEach((listing) => {
                if (listing.lat && listing.lng) {
                    bounds.extend(new google.maps.LatLng(Number(listing.lat), Number(listing.lng)));
                }
            });
            if (!bounds.isEmpty()) {
                setInitialBounds(bounds);
                map.fitBounds(bounds);
                const padding = { top: 50, right: 50, bottom: 50, left: 50 };
                map.fitBounds(bounds, padding);
            }
        }
        
        // Clear any existing timeout
        if (mapLoadTimeoutRef.current) {
            clearTimeout(mapLoadTimeoutRef.current);
        }
        
        // Set timeout to enable filtering after map settles
        mapLoadTimeoutRef.current = setTimeout(() => {
            isInitialLoadRef.current = false;
            console.log('Initial load complete - filtering enabled');
        }, 2000);
    };

    const filterListingsByBounds = async (map: google.maps.Map) => {
        const bounds = map.getBounds();
        if (!bounds) return;

        try {
            console.log('Filtering by bounds');
            const northeast = bounds.getNorthEast();
            const southwest = bounds.getSouthWest();

            const boundsData = {
                north: northeast.lat().toString(),
                south: southwest.lat().toString(),
                east: northeast.lng().toString(),
                west: southwest.lng().toString(),
            };

            let filters = {
                ...appliedFilters,
                ...boundsData,
                offset: 0,
                limit: limit
            }
            console.log('Bounds filter:', filters);
            const response = await api.post(`/co-listings`, filters);
            if (!response.data.status) {
                throw new Error(response.data.error)
            };
            console.log(response.data.listings)
            setListings(response.data.listings || []);
            setTotal(response.data.total || 0);
            setOffset(0);
            setHasMore(response.data.hasMore);
        } catch (err: any) {
            console.error('Error filtering listings by bounds:', err);
            toast.error('Failed to filter listings');
        }
    };

    const handleToggle = (setFn: any, value: any, multiple = false) => {
        setFn((prev: any) => {
          if (!multiple) return prev === value ? null : value
          return prev.includes(value) ? prev.filter((v: any) => v !== value) : [...prev, value]
        })
    }
    
    const handleApplyFilters = () => {
        const filters = {
            forSaleOrRentId: selectedType,
            listingTypeId: selectedCategory,
            stateId: selectedState,
            projectTypeId: selectedProjectType,
            propertyTypeId: selectedPropertyType,
            subTypeId: selectedSubType,
            priceRange: selectedPrice,
            search: searchQuery,
        };
        
        isInitialLoadRef.current = true;
        setAppliedFilters(filters);
        setOpen(false);
    };

    const handleMapChange = () => {
        if (isInitialLoadRef.current) {
            console.log('Skipping filter - still in initial load');
            return;
        }

        console.log('Map changed - filtering by bounds');
        if (mapInstance) {
            filterListingsByBounds(mapInstance);
        }
    };

    const handleMarkerClick = (listing: PropertyListings) => {
        setSelectedListing(listing);
        if (mapInstance && listing.lat && listing.lng) {
            mapInstance.panTo({ lat: Number(listing.lat), lng: Number(listing.lng) });
            mapInstance.setZoom(16);
        }
    };

    const handleCardClick = (listing: PropertyListings) => {
        setSelectedListing(listing);
        if (mapInstance && listing.lat && listing.lng) {
            mapInstance.panTo({ lat: Number(listing.lat), lng: Number(listing.lng) });
            mapInstance.setZoom(16);
        }
        setActiveTab('map');
    };

    const handleResetView = () => {
        if (mapInstance && initialBounds) {
            mapInstance.fitBounds(initialBounds);
            setSelectedListing(null);
        }
    };

    const toggleMapType = () => {
        const newType = mapType === 'roadmap' ? 'satellite' : 'roadmap';
        setMapType(newType);
        if (mapInstance) {
            mapInstance.setMapTypeId(newType);
        }
    };

    const handleSearch = () => {
        isInitialLoadRef.current = true;
        const newFilters = {
            ...appliedFilters,
            search: searchQuery
        };
        setAppliedFilters(newFilters);
    };

    const handleCloseInfoWindow = () => setSelectedListing(null);

    const mapOptions = useMemo<google.maps.MapOptions | undefined>(() => {
        if (!isLoaded || !window.google) return undefined;
      
        return {
            disableDefaultUI: true,
            zoomControl: false,
            mapTypeControl: false,
            mapTypeId: mapType,
            styles: [
                { featureType: 'poi', stylers: [{ visibility: 'off' }] },
                { featureType: 'transit', stylers: [{ visibility: 'off' }] },
                { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
            ],
        };
    }, [isLoaded, mapType]);

    const handleRequestCoShare = (listingId: number) => {
        setPendingListingId(listingId);
        setShowConfirmDialog(true);
    };

    const confirmCoShare = async () => {
        if (!pendingListingId) return;
        
        try {
            const response = await api.post(`/listings/${pendingListingId}/co-share`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setListings(prev =>
                prev.map(item =>
                    item.id === pendingListingId
                        ? { ...item, shared: response.data.coShare }
                        : item
                )
            );
            toast.success('You have sent a request to the listing agent.');
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        } finally {
            setShowConfirmDialog(false);
            setPendingListingId(null);
        }
    };

    const withdrawCoShare = async (listingId: number, coShareId: number) => {
        try {
            const response = await api.post(`/listings/${listingId}/co-share/${coShareId}/withdraw`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setListings(prev =>
                prev.map(item =>
                    item.id === listingId
                        ? { ...item, shared: null }
                        : item
                )
            );
            toast.success('Request withdrawn.');
        } catch (err : any) {
            const error = Helper.errorHandler(err);
            toast.error(error);
        }
    }

    const handlePreview = (listing: PropertyListings, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setPreviewListing(listing);
        setShowPreview(true);
    };

    // Infinite scroll handler
    const handleScroll = () => {
        if (!listViewRef.current || loading || !hasMore) return;
        
        const { scrollTop, scrollHeight, clientHeight } = listViewRef.current;
        
        // Load more when user is 200px from bottom
        if (scrollHeight - scrollTop - clientHeight < 200) {
            console.log('Near bottom, loading more listings...');
            getListings(true);
        }
    };

    useEffect(() => {
        getListingCriterias();
        getListings(false);
        return () => {
            if (mapLoadTimeoutRef.current) {
                clearTimeout(mapLoadTimeoutRef.current);
            }
            clearMarkers();
            if (mapInstance) {
                google.maps.event.clearInstanceListeners(mapInstance);
                setMapInstance(null);
            }
        };
    }, []);

    useEffect(() => {
        // Set initial load flag and get listings
        isInitialLoadRef.current = true;
        getListings(false);
        
        // Clear any existing timeout
        if (mapLoadTimeoutRef.current) {
            clearTimeout(mapLoadTimeoutRef.current);
        }
        
        // Reset the initial load flag after listings are loaded and map settles
        mapLoadTimeoutRef.current = setTimeout(() => {
            isInitialLoadRef.current = false;
            console.log('Filter applied - enabling map filtering');
        }, 2000);
    }, [appliedFilters]);

    useEffect(() => {
        if (mapInstance && listings.length > 0) {
            createMarkers(mapInstance, listings);
        }
    }, [listings, mapInstance]);

    useEffect(() => {
        if (activeTab === 'list') {
            setSelectedListing(null);
        }
    }, [activeTab]);

    // Add scroll listener
    useEffect(() => {
        const listView = listViewRef.current;
        if (listView && activeTab === 'list') {
            listView.addEventListener('scroll', handleScroll);
            return () => listView.removeEventListener('scroll', handleScroll);
        }
    }, [activeTab, loading, hasMore]);

    if (loading && listings.length === 0) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="w-full h-screen flex items-center justify-center">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
                    <p className="text-gray-500 text-sm">Loading map...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-screen flex flex-col">
            <div className='mb-4 flex justify-between items-center'>
                <h5 className='h5 uppercase bold'>Co-Share Listings</h5>
            </div>

            <div className="mb-4 flex flex-wrap gap-3">
                <div className="flex-1 min-w-[200px] relative">
                    <Input 
                    placeholder='Search here' 
                    value={searchQuery}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                        }
                    }}
                    onChange={(e) => setSearchQuery(e.target.value)}/>
                    <Button 
                    variant={'ghost'}
                    onClick={handleSearch} 
                    className="px-6 absolute top-1/2 -translate-y-1/2 right-0 hover:bg-gray-400">
                        <Search />
                    </Button>
                </div>
                <Drawer open={open} onOpenChange={setOpen}>
                    <DrawerTrigger asChild>
                        <Button variant="outline" className="border-none shadow-none">
                        <ListFilter color="lightgrey" />
                        </Button>
                    </DrawerTrigger>

                    <DrawerContent className="overflow-scroll no-scrollbar" ref={divRef}>
                        <DrawerHeader>
                            <DrawerTitle>Filter Listings</DrawerTitle>
                            <DrawerDescription>Select your preferred filters below.</DrawerDescription>
                        </DrawerHeader>
                        <div className="mt-4 flex justify-center px-4">
                            <div className="w-full max-w-xl space-y-8 pb-10">
                            <div>
                                <Label>Type</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                {
                                    criterias.forSaleOrRentOptions.map((opt: any) => (
                                        <Button
                                        key={opt.value}
                                        size="sm"
                                        variant={selectedType === opt.value ? "default" : "outline"}
                                        onClick={() => handleToggle(setSelectedType, opt.value)}>
                                        {opt.label}
                                        </Button>
                                    ))
                                }
                                </div>
                            </div>
                            <div>
                                <Label>Categories</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                {
                                    criterias.listingTypes.map((opt: any) => (
                                        <Button
                                        key={opt.value}
                                        size="sm"
                                        variant={selectedCategory === opt.value ? "default" : "outline"}
                                        onClick={() => handleToggle(setSelectedCategory, opt.value)}
                                        >
                                        {opt.label}
                                        </Button>
                                    ))
                                }
                                </div>
                            </div>

                            <div>
                                <Label>Areas</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                {
                                    criterias.states.map((state: any) => (
                                        <Button
                                        key={state.id}
                                        size="sm"
                                        variant={selectedState === state.id ? "default" : "outline"}
                                        onClick={() => handleToggle(setSelectedState, state.id)}
                                        >
                                        {state.name}
                                        </Button>
                                    ))
                                    }
                                </div>
                            </div>
                            <div>
                                <Label>Project Type</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                {
                                    criterias.projectTypes.map((type: any) => (
                                        <Button
                                        key={type.value}
                                        size="sm"
                                        variant={selectedProjectType === type.value ? "default" : "outline"}
                                        onClick={() => handleToggle(setSelectedProjectType, type.value)}>
                                        {type.label}
                                        </Button>
                                    ))
                                }
                                </div>
                            </div>
                            <div>
                                <Label>Property Type</Label>
                                <div className="mt-2 flex flex-wrap gap-2">
                                {
                                    criterias.propertyTypes.map((type: any) => (
                                        <Button
                                        key={type.id}
                                        size="sm"
                                        variant={selectedPropertyType === type.id ? "default" : "outline"}
                                        onClick={() => setSelectedPropertyType(type.id)}>
                                        {type.name}
                                        </Button>
                                    ))
                                }
                                </div>
                            </div>
                            {
                                selectedPropertyType && filteredSubTypes.length > 0 && (
                                    <div>
                                        <Label>Property Sub-Type</Label>
                                        <div className="mt-2 flex flex-wrap gap-2">
                                            {
                                                filteredSubTypes.map((sub: any) => (
                                                    <Button
                                                    key={sub.id}
                                                    size="sm"
                                                    variant={selectedSubType === sub.slug ? "default" : "outline"}
                                                    onClick={() => handleToggle(setSelectedSubType, sub.slug)} >
                                                    {sub.name}
                                                    </Button>
                                                ))
                                            }
                                        </div>
                                    </div>
                                )
                            }

                            <div className="w-full max-w-md">
                                <Label>Price Range (RM) {selectedPrice ? '' : '- No filter applied'}</Label>
                                <Slider
                                min={0}
                                max={10000000}
                                step={10000}
                                value={selectedPrice || [100000, 500000]}
                                onValueChange={(val) => setSelectedPrice(val as [number, number])}
                                className="mt-2 cursor-pointer" />
                                <div className="text-sm text-muted-foreground mt-2">
                                    {
                                        selectedPrice ? 
                                        (
                                            <>
                                                RM {Helper.formatWithoutDecimal(selectedPrice[0])} – RM{" "}
                                                {Helper.formatWithoutDecimal(selectedPrice[1])}+
                                            </>
                                        ) 
                                        : 
                                        (
                                            <span className="text-gray-400">Move slider to set price range</span>
                                        )
                                    }
                                </div>
                            </div>
                            <div className="flex justify-between pt-6 border-t">
                                <Button variant="outline" onClick={handleReset}>
                                Reset
                                </Button>
                                <Button onClick={handleApplyFilters} disabled={applying}>
                                {applying ? <Spinner /> : "Apply"}
                                </Button>
                            </div>
                            </div>
                        </div>
                    </DrawerContent>
                </Drawer>
            </div>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Co-Share Agreement</AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3">
                            <p>By requesting to co-share this listing, you agree to share 50% of the commission/profit with the listing agent.</p>
                            <p className="font-medium text-gray-900">Do you agree to these terms?</p>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => {
                            setShowConfirmDialog(false);
                            setPendingListingId(null);
                        }}>
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction onClick={confirmCoShare}>
                            I Agree
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <Dialog open={showPreview} onOpenChange={setShowPreview}>
                <DialogContent className="max-w-7xl w-full h-[95vh] p-0">
                    <div className="h-full overflow-auto p-6">
                        {previewListing && <Details listing={previewListing} size='sm'/>}
                    </div>
                </DialogContent>
            </Dialog>

            <div className="lg:hidden mb-4 flex gap-2 border-b">
                <button
                onClick={() => setActiveTab('map')}
                className={`cursor-pointer flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'map' 
                        ? 'text-black-600 border-b-2 border-gray-600' 
                        : 'text-gray-500 hover:text-gray-700'
                }`}>
                    <MapIcon size={18} />
                    Map View
                </button>
                <button
                onClick={() => setActiveTab('list')}
                className={`cursor-pointer flex-1 flex items-center justify-center gap-2 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'list' 
                        ? 'text-black-600 border-b-2 border-gray-600' 
                        : 'text-gray-500 hover:text-gray-700'
                }`}>
                    <List size={18} />
                    List View ({listings.length})
                </button>
            </div>
            <div className="flex-1 flex flex-col lg:flex-row gap-4 overflow-hidden">
                {/* <div className={`w-full lg:w-1/2 h-full relative rounded-xl overflow-hidden ${
                    activeTab === 'map' ? 'block' : 'hidden lg:block'
                }`}>
                    <div className="absolute top-4 left-4 z-10 flex flex-col gap-2">
                        <Button
                        className="bg-white hover:bg-gray-100 text-gray-700 shadow-lg text-start"
                        size="sm">
                           {listings?.length || 0} property found.
                        </Button>
                        {
                            selectedListing && (
                                <Button
                                    onClick={handleResetView}
                                    className="bg-white hover:bg-gray-100 text-gray-700 shadow-lg"
                                    size="sm">
                                    <MapIcon size={16} className="mr-2" />
                                    Reset View
                                </Button>
                            )
                        }
                        <Button
                            onClick={toggleMapType}
                            className="bg-white hover:bg-gray-100 text-gray-700 shadow-lg text-start"
                            size="sm">
                            {
                                mapType === 'roadmap' ? (
                                    <>
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        Satellite
                                    </>
                                ) : (
                                    <>
                                        <MapIcon size={16} className="mr-2" />
                                        Map
                                    </>
                                )
                            }
                        </Button>
                    </div>
                    <div className="absolute bottom-4 right-4 z-10 flex flex-col gap-1 bg-white rounded-lg shadow-lg overflow-hidden">
                        <button
                        onClick={() => {
                            mapInstance?.setZoom((mapInstance.getZoom() || 11) + 1)
                        }}
                        className="hover:bg-gray-100 text-gray-700 px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center cursor-pointer"
                        title="Zoom in">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                        </button>
                        <button
                        onClick={() => {
                            mapInstance?.setZoom((mapInstance?.getZoom() || 11) - 1)
                        }}
                        className="hover:bg-gray-100 text-gray-700 px-3 py-2 text-sm font-medium transition-colors flex items-center justify-center cursor-pointer"
                        title="Zoom out">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                        </button>
                    </div>
                    <GoogleMap
                    mapContainerStyle={{ width: '100%', height: '100%' }}
                    center={mapCenter}
                    zoom={mapZoom}
                    onLoad={handleMapLoad}
                    onDragEnd={handleMapChange}
                    onZoomChanged={handleMapChange}
                    options={mapOptions}>
                        {
                            listings.map(
                                (listing) =>
                                Number(listing.lat) &&
                                Number(listing.lng) && (
                                    <React.Fragment key={listing.id}>
                                        <Circle
                                        center={{ 
                                            lat: Number(listing.lat),
                                            lng: Number(listing.lng)
                                        }}
                                        radius={Number(listing.radius || 50)}
                                        options={{
                                            fillColor: '#4285F4',
                                            fillOpacity: 0.2,
                                            strokeColor: '#4285F4',
                                            strokeOpacity: 0.8,
                                            strokeWeight: 2,
                                            editable: false,
                                            draggable: false,
                                        }}/>
                                        {
                                            selectedListing?.id === listing.id && (
                                                <OverlayView
                                                    position={{
                                                        lat: Number(listing.lat),
                                                        lng: Number(listing.lng),
                                                    }}
                                                    mapPaneName={OverlayView.FLOAT_PANE}>
                                                    <div className="relative -translate-y-[calc(100%+10px)] -translate-x-1/2" style={{ zIndex: 1001 }}>
                                                        <div className="bg-white rounded-lg shadow-xl border overflow-hidden w-[180px] lg:w-[220px]">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleCloseInfoWindow();
                                                                }}
                                                                className="absolute top-1 right-1 z-10 bg-white rounded-full p-1 text-gray-600 hover:text-gray-900 shadow-md hover:shadow-lg transition-all">
                                                                <X size={12} />
                                                            </button>
                                                            <div className="cursor-default">
                                                                {
                                                                    listing.medias && listing.medias.length > 0 && (
                                                                        <div className="relative h-20 lg:h-28 bg-gray-100">
                                                                            <img
                                                                                src={`${import.meta.env.VITE_AWS_S3}/${
                                                                                    listing.medias[0].path
                                                                                }`}
                                                                                alt={listing.headline}
                                                                                className="w-full h-full object-cover"
                                                                                onError={(e) =>
                                                                                    (e.currentTarget.style.display = 'none')
                                                                                }
                                                                            />
                                                                        </div>
                                                                    )
                                                                }
                                                                <div className="p-2">
                                                                    <div className="text-xs lg:text-sm font-bold text-gray-900 mb-0.5">
                                                                        RM {Helper.formatWithoutDecimal(Number(listing.price))}
                                                                    </div>
                                                                    <div className="text-xs font-medium text-gray-700 line-clamp-1 mb-0.5">
                                                                        {listing.headline}
                                                                    </div>
                                                                    <div className="text-[10px] lg:text-xs text-gray-500 line-clamp-1 mb-1">
                                                                        {listing.rawAddress}
                                                                    </div>
                                                                    {
                                                                        (listing.noOfBedrooms || listing.noOfBathrooms || listing.noOfParkingSpots) && (
                                                                            <div className="flex items-center gap-2 text-gray-600 mb-1 text-[10px]">
                                                                            {listing.noOfBedrooms && (
                                                                                <div className="flex items-center gap-1">
                                                                                <Bed className="w-3 h-3" />
                                                                                <span>{listing.noOfBedrooms}</span>
                                                                                </div>
                                                                            )}
                                                                            {listing.noOfBathrooms && (
                                                                                <div className="flex items-center gap-1">
                                                                                <Bath className="w-3 h-3" />
                                                                                <span>{listing.noOfBathrooms}</span>
                                                                                </div>
                                                                            )}
                                                                            {listing.noOfParkingSpots && (
                                                                                <div className="flex items-center gap-1">
                                                                                <Car className="w-3 h-3" />
                                                                                <span>{listing.noOfParkingSpots}</span>
                                                                                </div>
                                                                            )}
                                                                            </div>
                                                                        )
                                                                    }
                                                                    {
                                                                        <div className='flex gap-2 items-center mt-3'>
                                                                            <Avatar className="rounded-full">
                                                                                <AvatarImage
                                                                                src={`${import.meta.env.VITE_AWS_S3}/${listing.agent.avatar}`}/>
                                                                                <AvatarFallback>
                                                                                    {listing.agent.firstName[0]}
                                                                                    {listing.agent.lastName[0]}
                                                                                </AvatarFallback>
                                                                            </Avatar>
                                                                            <div>
                                                                                <p className='text-xs font-semibold text-black'>{listing.agent.firstName} {listing.agent.lastName}</p>
                                                                                <p className='text-xs font-light text-black'>{listing.agent.renNo}</p>
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </OverlayView>
                                            )
                                        }
                                    </React.Fragment>
                                )
                            )
                        }
                    </GoogleMap>
                </div> */}
                <div 
                    ref={listViewRef}
                    className={`w-full px-1 overflow-y-auto ${
                        activeTab === 'list' ? 'block' : 'hidden lg:block'
                    }`}>
                    {
                        listings.length > 0 && (
                            <div className="mb-3 text-sm text-gray-600">
                                Showing {listings.length} of {total} listings
                            </div>
                        )
                    }
                    <div className="grid grid-cols-1 md:grid-cols-4 sm:grid-cols-2 gap-3 pb-4">
                        {
                            listings.map((listing) => (
                                <div 
                                    key={listing.id}
                                    onClick={() => handleCardClick(listing)}
                                    className={`bg-white overflow-hidden rounded-lg shadow-md cursor-pointer transition-all hover:shadow-xl ${
                                        selectedListing?.id === listing.id ? 'ring-2 ring-blue-500 shadow-xl' : ''
                                    }`}>
                                    {
                                        listing.medias && listing.medias.length > 0 && (
                                            <div className="relative h-32 lg:h-36 bg-gray-100 relative">
                                                <img
                                                    src={`${import.meta.env.VITE_AWS_S3}/${listing.medias[0].path}`}
                                                    alt={listing.headline}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => (e.currentTarget.style.display = 'none')}
                                                />
                                                <Badge variant={'default'} className='absolute top-1 left-1'>
                                                    {listing.forSaleOrRentId === 1 ? 'SALE' : 'RENT'}
                                                </Badge>
                                            </div>
                                        )
                                    }
                                    <div className="p-2.5 lg:p-3">
                                        <div className="text-sm font-bold text-gray-900">
                                            RM {Helper.formatWithoutDecimal(Number(listing.price))}
                                        </div>
                                        <div className="text-xs lg:text-sm font-medium text-gray-700 line-clamp-2">
                                            {listing.headline}
                                        </div>
                                        {/*
                                        <div className="text-xs text-gray-500 line-clamp-1">
                                            {listing.rawAddress}
                                        </div>
                                        */}
                                        {
                                            (listing.noOfBedrooms || listing.noOfBathrooms || listing.noOfParkingSpots) && (
                                                <div className="flex items-center gap-2.5 text-xs text-gray-600 mt-2">
                                                    {
                                                        listing.noOfBedrooms && (
                                                            <div className="flex items-center gap-1">
                                                                <Bed size={13} />
                                                                <span>{listing.noOfBedrooms}</span>
                                                            </div>
                                                        )
                                                    }
                                                    {
                                                        listing.noOfBathrooms && (
                                                            <div className="flex items-center gap-1">
                                                                <Bath size={13} />
                                                                <span>{listing.noOfBathrooms}</span>
                                                            </div>
                                                        )
                                                    }
                                                    {
                                                        listing.noOfParkingSpots && (
                                                            <div className="flex items-center gap-1">
                                                                <Car size={13} />
                                                                <span>{listing.noOfParkingSpots}</span>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                            )
                                        }
                                        {
                                            <div className="flex items-start justify-between gap-3 mt-3">
                                                <div className="flex gap-2 items-center shrink-0 min-w-0">
                                                    <Avatar className="rounded-full w-10 h-10 shrink-0">
                                                        <AvatarImage
                                                        src={`${import.meta.env.VITE_AWS_S3}/${listing.agent.avatar}`}
                                                        />
                                                        <AvatarFallback>
                                                        {listing.agent.firstName[0]}
                                                        {listing.agent.lastName[0]}
                                                        </AvatarFallback>
                                                    </Avatar>
                                            
                                                    <div className="min-w-0">
                                                        <p className="text-xs font-semibold truncate">
                                                        {listing.agent.firstName} {listing.agent.lastName}
                                                        </p>
                                                        <p className="text-xs text-gray-500 truncate">
                                                        {listing.agent.renNo}
                                                        </p>
                                                    </div>
                                                </div>
                                            
                                                <div className="flex flex-wrap gap-1.5 justify-end">
                                                    <Button
                                                    variant="outline"
                                                    size="sm"

                                                    onClick={(e) => handlePreview(listing, e)}>
                                                        <Eye className="w-3.5 h-3.5" />
                                                        <span className="text-xs">Preview</span>
                                                    </Button>
                                                    {
                                                        listing.createdBy !== agent.id && (
                                                            <>
                                                                {!listing.shared || listing.shared?.status === 99 ? (
                                                                    <Button

                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        handleRequestCoShare(listing.id);
                                                                    }}
                                                                    >
                                                                    <Send className="w-3.5 h-3.5" />
                                                                    <span className="text-xs">Request</span>
                                                                    </Button>
                                                                ) : listing.shared.status === 0 ? (
                                                                    <Button
                                                                    className="bg-green-50 border-green-300 text-green-700 hover:bg-green-100"
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        withdrawCoShare(listing.id, listing.shared.id);
                                                                    }}
                                                                    >
                                                                    <span className="text-xs">Requested</span>
                                                                    <X className="w-3.5 h-3.5" />
                                                                    </Button>
                                                                ) : listing.shared.status === 1 && (
                                                                    <Link to={`/listings/view/${listing.id}`} onClick={(e) => e.stopPropagation()}>
                                                                    <Button

                                                                        variant="default"
                                                                        size="sm"
                                                                    >
                                                                        <span className="text-xs">View</span>
                                                                    </Button>
                                                                    </Link>
                                                                )}
                                                            </>
                                                        )
                                                    }
                                                </div>
                                            </div>
                                        }
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                    {
                        loading && listings.length > 0 && (
                            <div className="text-center py-6">
                                <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                                <p className="text-xs lg:text-sm font-medium text-blue-700">Loading more...</p>
                            </div>
                        )
                    }
                    {
                        !hasMore && listings.length > 0 && (
                            <div className="text-center py-6 text-sm text-gray-500">
                                No more listings to load
                            </div>
                        )
                    }
                    {
                        listings.length === 0 && !loading && (
                            <div className="text-center py-12">
                                <p className="text-gray-500">No listings found</p>
                            </div>
                        )
                    }
                </div>
            </div>
        </div>
    );
};

export default CoListings;